import {
  Badge,
  Card,
  cn,
  EmptyState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Spinner,
} from "@forge/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ExternalLink, TrendingUp } from "lucide-react";
import { useState, type ReactNode } from "react";
import { PageHeader } from "../../components/page-header";
import { api, unwrap } from "../../lib/api";

type TrendBucket = "growing" | "wanted" | "popular";

const BUCKET_LABEL: Record<TrendBucket, string> = {
  growing: "Crescendo",
  wanted: "Mais buscadas",
  popular: "Populares",
};

export const Route = createFileRoute("/_app/demand")({
  validateSearch: (search: Record<string, unknown>) => ({
    accountId:
      typeof search.accountId === "string" ? search.accountId : undefined,
    categoryId:
      typeof search.categoryId === "string" ? search.categoryId : undefined,
  }),
  component: DemandPage,
});

function categoryLabel(row: { id: string; name?: string | null }) {
  return row.name?.trim() || row.id;
}

function DemandPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [bucket, setBucket] = useState<TrendBucket>("growing");

  const demand = useQuery({
    queryKey: ["demand", search.accountId, search.categoryId],
    queryFn: async () =>
      unwrap(
        await api.demand.get({
          query: {
            accountId: search.accountId,
            categoryId: search.categoryId,
          },
        }),
      ),
  });

  const data = demand.data;
  const keywords = data?.trends[bucket] ?? [];
  const selectedCategory = data?.categories.find(
    (row) => row.id === data.categoryId,
  );
  const hero =
    data?.own?.position != null ? `#${data.own.position}` : "Fora do top 20";

  return (
    <>
      <PageHeader
        parent={
          <Link
            to="/listings"
            className="text-2xs font-medium text-ink-muted transition-colors duration-100 hover:text-ink"
          >
            Anúncios
          </Link>
        }
        title={data?.categoryName ?? "Demanda"}
        description="Buscas e ranking oficiais da semana no Mercado Livre"
        actions={
          data && data.categories.length > 1 ? (
            <Select
              value={data.categoryId}
              itemToStringLabel={(value) => {
                const row = data.categories.find((item) => item.id === value);
                return row ? categoryLabel(row) : String(value);
              }}
              onValueChange={(value) => {
                void navigate({
                  search: {
                    accountId: data.accountId ?? undefined,
                    categoryId: value as string,
                  },
                });
              }}
            >
              <SelectTrigger placeholder="Categoria" />
              <SelectContent>
                {data.categories.map((row) => (
                  <SelectItem key={row.id} value={row.id}>
                    <span className="min-w-0 truncate">
                      {categoryLabel(row)}
                    </span>
                    <span className="ml-2 font-mono text-ink-faint">
                      {row.count}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null
        }
      />

      {demand.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-5 text-ink-muted" />
        </div>
      ) : !data || data.categories.length === 0 ? (
        <Card>
          <EmptyState
            icon={<TrendingUp />}
            title="Sem categoria ainda"
            description="Reimporte os anúncios em Conexões para gravar a categoria e ver o radar."
            action={
              <Link
                to="/connections"
                className="text-2xs font-medium text-ember-strong hover:underline"
              >
                Ir para Conexões
              </Link>
            }
          />
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          <Card className="animate-rise">
            <div className="flex flex-col gap-1 p-4">
              <p className="text-2xs font-medium uppercase tracking-wider text-ink-muted">
                Sua posição
                {selectedCategory
                  ? ` · ${categoryLabel(selectedCategory)}`
                  : ""}
              </p>
              <p
                className={cn(
                  "font-mono text-2xl font-medium tracking-tight",
                  data.own ? "text-ink" : "text-danger",
                )}
              >
                {hero}
              </p>
              <p className="mt-0.5 text-[0.8125rem] text-ink-secondary">
                {data.own
                  ? "Melhor posição entre os seus anúncios nesta categoria."
                  : "Nenhum anúncio seu está no top 20 de mais vendidos."}
              </p>
            </div>
          </Card>

          <div className="grid items-start gap-3 lg:grid-cols-2">
            <Card className="min-w-0">
              <div className="flex items-center gap-1 border-b border-line px-3 py-2">
                {(["growing", "wanted", "popular"] as const).map((key) => (
                  <FilterChip
                    key={key}
                    selected={bucket === key}
                    onClick={() => setBucket(key)}
                  >
                    {BUCKET_LABEL[key]}
                  </FilterChip>
                ))}
              </div>
              {keywords.length === 0 ? (
                <EmptyState
                  icon={<TrendingUp />}
                  title="Sem buscas nesta fatia"
                  description="O Mercado Livre atualiza as 50 buscas da semana. Tente outra categoria."
                />
              ) : (
                <ol className="divide-y divide-line">
                  {keywords.map((row, index) => (
                    <li key={`${row.keyword}-${index}`}>
                      {row.url ? (
                        <a
                          href={row.url}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center justify-between gap-3 px-4 py-2.5 text-[0.8125rem] text-ink [@media(hover:hover)_and_(pointer:fine)]:hover:bg-surface-sunken"
                        >
                          <span className="min-w-0 truncate">{row.keyword}</span>
                          <span className="font-mono text-2xs text-ink-faint">
                            {index + 1}
                          </span>
                        </a>
                      ) : (
                        <div className="flex items-center justify-between gap-3 px-4 py-2.5 text-[0.8125rem] text-ink">
                          <span className="min-w-0 truncate">{row.keyword}</span>
                          <span className="font-mono text-2xs text-ink-faint">
                            {index + 1}
                          </span>
                        </div>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </Card>

            <Card className="min-w-0 overflow-hidden">
              <p className="border-b border-line px-4 py-2 text-2xs font-medium text-ink-muted">
                Mais vendidos
              </p>
              {data.ranking.length === 0 ? (
                <EmptyState
                  icon={<TrendingUp />}
                  title="Ranking indisponível"
                  description="O top 20 só existe em categorias folha. Reimporte se a categoria estiver vazia."
                />
              ) : (
                <ol className="divide-y divide-line">
                  {data.ranking.map((row) => {
                    const title = row.title?.trim() || null;
                    const visitUrl = row.permalink;
                    const label = title ?? row.id;
                    const inner = (
                      <>
                        <span
                          title={label}
                          className={
                            title
                              ? "block truncate font-medium text-ink"
                              : "block truncate font-mono text-2xs text-ink-muted"
                          }
                        >
                          {label}
                        </span>
                        {title ? (
                          <span
                            title={row.id}
                            className="block truncate font-mono text-2xs text-ink-faint"
                          >
                            {row.id}
                          </span>
                        ) : null}
                      </>
                    );
                    return (
                      <li
                        key={`${row.type}-${row.id}`}
                        className={cn(
                          "flex min-w-0 items-center gap-3 px-4 py-2.5",
                          row.mine && "bg-surface-sunken",
                        )}
                      >
                        <span className="w-6 shrink-0 font-mono text-2xs tabular-nums text-ink-faint">
                          {row.position}
                        </span>
                        <div className="min-w-0 flex-1 overflow-hidden">
                          {row.listingId ? (
                            <Link
                              to="/listings/$id"
                              params={{ id: row.listingId }}
                              className="block min-w-0"
                            >
                              {inner}
                            </Link>
                          ) : visitUrl ? (
                            <a
                              href={visitUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="block min-w-0 [@media(hover:hover)_and_(pointer:fine)]:hover:text-ember-strong"
                            >
                              {inner}
                            </a>
                          ) : (
                            <div className="min-w-0">{inner}</div>
                          )}
                        </div>
                        {row.mine ? <Badge className="shrink-0">Você</Badge> : null}
                        {visitUrl ? (
                          <a
                            href={visitUrl}
                            target="_blank"
                            rel="noreferrer"
                            aria-label="Abrir no Mercado Livre"
                            className="shrink-0 text-ink-muted transition-transform duration-150 ease-swift active:scale-[0.97] [@media(hover:hover)_and_(pointer:fine)]:hover:text-ink"
                          >
                            <ExternalLink className="size-3.5" />
                          </a>
                        ) : null}
                      </li>
                    );
                  })}
                </ol>
              )}
            </Card>
          </div>
        </div>
      )}
    </>
  );
}

function FilterChip({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        "inline-flex h-7 items-center gap-1 rounded-full px-2.5 text-2xs font-medium",
        "transition-transform duration-150 ease-swift active:scale-[0.97]",
        selected
          ? "bg-ember-subtle text-ember-strong"
          : "text-ink-secondary [@media(hover:hover)_and_(pointer:fine)]:hover:bg-surface-sunken",
      )}
    >
      {children}
    </button>
  );
}
